import { Injectable } from '@angular/core';
import { Device, DeviceOperationsService, IntegrationMDMProxyService, ModelNameFlags } from '@era/locations-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import * as DeviceActions from './device.actions';

import { Store } from '@ngrx/store';
import * as DeviceStoreSelectors from './device.selector';
import { getDevicesFilteredOnSubset } from './device.utils';
import { LocationSubset } from './location-subset';
import { safeMap } from '../effects.helpers';

@Injectable()
export class DeviceRebootEffects {
  constructor(
    private readonly integrationMDMProxyService: IntegrationMDMProxyService,
    private readonly actions$: Actions,
    private readonly deviceOperationsService: DeviceOperationsService,
    private readonly deviceStore: Store<Device>
  ) {}

  deviceRebootEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceActions.DeviceDetailsContextMenuActions.deviceRestartCommandSent),
      mergeMap(({ udid }) =>
        safeMap(
          this.integrationMDMProxyService.queueRestartDeviceCommand(udid),
          () => DeviceActions.DeviceApiActions.deviceRestartSuccessful(),
          (error) => DeviceActions.DeviceApiActions.failedToRestartDevice({ error })
        )
      )
    );
  });

  devicesRebootEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceActions.DeviceModalActions.rebootDevicesRequested),
      mergeMap(({ locationSubset, modelNames }) =>
        getDevicesFilteredOnSubset(this.deviceStore, locationSubset).pipe(
          take(1),
          map((devices) => this.filterDevicesOnType(devices, modelNames)),
          map((devices) => devices.map((device) => device.deviceId)),
          mergeMap((deviceIds) =>
            safeMap(
              this.deviceOperationsService.queueRestartDevicesCommand({ selector: { devices: deviceIds } }),
              () => DeviceActions.DeviceApiActions.restartDevicesRequestSuccessful(),
              (error) => DeviceActions.DeviceApiActions.restartDevicesError({ error })
            )
          )
        )
      )
    );
  });

  getDevicesFilteredOnSubset(locationSubset: LocationSubset): Observable<Device[]> {
    switch (locationSubset) {
      case LocationSubset.All:
        return this.deviceStore.select(DeviceStoreSelectors.selectCurrentLocationDevices);
      case LocationSubset.Assigned:
        return this.deviceStore.select(DeviceStoreSelectors.selectCurrentLocationAssignedDevices);
      case LocationSubset.Spare:
        return this.deviceStore.select(DeviceStoreSelectors.selectCurrentLocationSpareDevices);
      case LocationSubset.Swapped:
        return this.deviceStore.select(DeviceStoreSelectors.selectCurrentLocationSwappedDevices);
      case LocationSubset.Housekeeping:
        return this.deviceStore.select(DeviceStoreSelectors.selectCurrentLocationHousekeepingDevices);
      case LocationSubset.Staff:
        return this.deviceStore.select(DeviceStoreSelectors.selectCurrentLocationStaffDevices);
      case LocationSubset.FrontDesk:
        return this.deviceStore.select(DeviceStoreSelectors.selectCurrentLocationFrontDeskDevices);
      case LocationSubset.Bar:
        return this.deviceStore.select(DeviceStoreSelectors.selectCurrentLocationBarDevices);
      case LocationSubset.Kiosk:
        return this.deviceStore.select(DeviceStoreSelectors.selectCurrentLocationKioskDevices);
    }
  }

  filterDevicesOnType(devices: Device[], modelNames: Set<ModelNameFlags>): Device[] {
    return devices.filter((device) =>
      [...modelNames].some((modelName) =>
        // TODO: update this when devices contain 1 modelName instead of the current array.
        [...(device?.apple?.modelName ?? [])].includes(modelName)
      )
    );
  }
}
